import { useCallback, useState } from 'react';
import api from '../../../../services/api';
import { fetchCurrentArticleTags, mergeTags } from '../../../../utils/tagUtils';

const useArticleModalTags = ({
  article,
  applyArticleUpdate,
  onAddTags,
  onRemoveTag,
  refreshGlobalTags,
  setLocalArticle
}) => {
  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [similarDialogOpen, setSimilarDialogOpen] = useState(false);
  const [lastAddedTag, setLastAddedTag] = useState(null);

  const openTagDialog = () => {
    setTagDialogOpen(true);
  };

  const closeTagDialog = () => {
    setTagDialogOpen(false);
  };

  const closeSimilarDialog = () => {
    setSimilarDialogOpen(false);
    setLastAddedTag(null);
  };

  const handleRemoveTag = useCallback(async (articleId, tag) => {
    try {
      setLocalArticle(prevArticle => ({
        ...prevArticle,
        tags: (prevArticle?.tags || []).filter(currentTag => {
          const tagId = typeof currentTag === 'object' ? currentTag._id : currentTag;
          const removeTagId = typeof tag === 'object' ? tag._id : tag;
          return tagId !== removeTagId;
        })
      }));

      if (onRemoveTag) {
        await onRemoveTag(articleId, tag);
      }
    } catch (error) {
      setLocalArticle(article);
      console.error('Erro ao remover tag:', error);
    }
  }, [article, onRemoveTag, setLocalArticle]);

  const handleAddTags = useCallback(async (newTags) => {
    try {
      if (!article?._id) {
        throw new Error('Artigo não selecionado para adicionar tags');
      }

      if (!onAddTags) {
        throw new Error('Ação de adicionar tags não configurada');
      }

      const currentTags = await fetchCurrentArticleTags(article._id);
      const mergedTags = mergeTags(currentTags, newTags);

      const tagsToCreate = mergedTags.filter(tag => !tag._id);
      const existingTags = mergedTags.filter(tag => tag._id);
      let createdTags = [];

      if (tagsToCreate.length > 0) {
        const createPromises = tagsToCreate.map(tag => api.post('/tags', { name: tag.name }));
        const createdResponses = await Promise.all(createPromises);
        createdTags = createdResponses.map(res => res.data.data);
      }

      const allTags = [...existingTags, ...createdTags];

      await onAddTags(article._id, allTags);

      if (createdTags.length > 0) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        await refreshGlobalTags();
      }

      const updatedArticleRes = await api.get(`/articles/${article._id}`);
      applyArticleUpdate(updatedArticleRes.data.data);
      setTagDialogOpen(false);

      if (allTags.length > 0) {
        setLastAddedTag(allTags[0]);
        setSimilarDialogOpen(true);
      }
    } catch (error) {
      console.error('Erro ao adicionar tags:', error);
      alert('Erro ao adicionar tags: ' + (error.message || 'Erro desconhecido'));
    }
  }, [article, applyArticleUpdate, onAddTags, refreshGlobalTags]);

  return {
    closeSimilarDialog,
    closeTagDialog,
    handleAddTags,
    handleRemoveTag,
    lastAddedTag,
    openTagDialog,
    similarDialogOpen,
    tagDialogOpen
  };
};

export default useArticleModalTags;
